import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { CheckboxFilter } from "@entities/global-discovery/ui/Filters/CheckboxFilter";
import { InputNumberFilter } from "@entities/global-discovery/ui/Filters/InputNumberFilter";
import { ListFilter } from "@entities/global-discovery/ui/Filters/ListFilter";
import { MultiListFilter } from "@entities/global-discovery/ui/Filters/MultiListFilter";
import { RangeFilter } from "@entities/global-discovery/ui/Filters/RangeFilter";
import { SelectRangeFilter } from "@entities/global-discovery/ui/Filters/SelectRangeFilter";
import { TagsFilter } from "@entities/global-discovery/ui/Filters/TagsFilter";
import { ConfigGlobalTypeSingle } from "@shared/lib/types";
import { useMemo } from "react";
import styles from "./AdvancedFilters.module.scss";

interface AdvancedFiltersProps {
  filters: ConfigGlobalTypeSingle[];
}

const FILTER_TYPE_LABELS: Record<string, string> = {
  creator: "Creator",
  content: "Content",
  performance: "Performance",
  audience: "Audience",
};

export function AdvancedFilters({ filters }: AdvancedFiltersProps) {
  const {
    isLoadingData,
    selectedFilters,
    backendFilters,
    resetSelectedFilters,
  } = useGetGlobalDiscoveryStore();

  // Check if filters have been modified
  const hasUnsavedFilters =
    JSON.stringify(selectedFilters) !== JSON.stringify(backendFilters);

  // Group filters by fType
  const groupedFilters = useMemo(() => {
    const groups: Record<string, ConfigGlobalTypeSingle[]> = {};
    filters.forEach((filter) => {
      const fType = filter.fType || "other";
      if (!groups[fType]) {
        groups[fType] = [];
      }
      if (!filter.hideFilter) {
        groups[fType].push(filter);
      }
    });
    return groups;
  }, [filters]);

  const handleClearAll = () => {
    resetSelectedFilters();
  };

  const renderFilter = (filter: ConfigGlobalTypeSingle) => {
    switch (filter.compType) {
      case "range":
        return <RangeFilter key={filter.facetName} filter={filter} />;
      case "inputNumber":
        return <InputNumberFilter key={filter.facetName} filter={filter} />;
      case "list":
        return <ListFilter key={filter.facetName} filter={filter} />;
      case "multiList":
        return <MultiListFilter key={filter.facetName} filter={filter} />;
      case "tags":
        return <TagsFilter key={filter.facetName} filter={filter} />;
      case "checkbox":
        return <CheckboxFilter key={filter.facetName} filter={filter} />;
      case "selectRange":
        return <SelectRangeFilter key={filter.facetName} filter={filter} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.advancedFilters}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <h3 className={styles.title}>Filters</h3>
          <button
            onClick={handleClearAll}
            className={styles.clearAllButton}
            type="button"
          >
            Clear all
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className={styles.content}>
        {Object.entries(groupedFilters).map(([fType, typeFilters]) => (
          <div key={fType}>
            <h4 className={styles.sectionTitle}>
              {FILTER_TYPE_LABELS[fType] || fType}
            </h4>
            <div className={styles.sectionContent}>
              {typeFilters.map((filter) => renderFilter(filter))}
            </div>
            {Object.keys(groupedFilters).indexOf(fType) <
              Object.keys(groupedFilters).length - 1 && (
              <div className={styles.divider} />
            )}
          </div>
        ))}
      </div>

      {/* Footer with Apply Button */}
    </div>
  );
}
